import React from 'react';
import { AlertTriangle, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { tone, toneForPriority } from "components/ui/tone";

const PriorityIndicator = ({ priority, showLabel = true, size = 'sm', ariaLabel }) => {
  const getPriorityConfig = (priority) => {
    // Labels, icons and descriptions stay local — they are this component's
    // content. Only the four colour fields move to the shared urgency ramp,
    // so a "high" ticket here matches a "high" test in A/B Testing.
    const META = {
      critical: { label: 'Critical', icon: AlertTriangle, description: 'Critical priority - requires immediate attention' },
      high:     { label: 'High',     icon: ArrowUp,       description: 'High priority - important issue' },
      medium:   { label: 'Medium',   icon: Minus,         description: 'Medium priority - standard issue' },
      low:      { label: 'Low',      icon: ArrowDown,     description: 'Low priority - minor issue' },
    };
    const meta = META[priority] ?? { label: 'Unknown', icon: Minus, description: 'Unknown priority level' };
    const t = tone(toneForPriority(priority));

    return {
      ...meta,
      bgColor: t.surface,
      textColor: t.fg,
      iconColor: t.fg,
      borderColor: t.border,
    };
  };

  const getSizeClasses = (size) => {
    switch (size) {
      case 'xs':
        return {
          container: 'px-1.5 py-0.5 text-xs',
          icon: 'w-3 h-3'
        };
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'w-3 h-3'
        };
      case 'md':
        return {
          container: 'px-2.5 py-1 text-sm',
          icon: 'w-4 h-4'
        };
      case 'lg':
        return {
          container: 'px-3 py-1.5 text-sm',
          icon: 'w-4 h-4'
        };
      default:
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'w-3 h-3'
        };
    }
  };

  const config = getPriorityConfig(priority);
  const sizeClasses = getSizeClasses(size);
  const Icon = config.icon;

  // Icon-only version for compact display
  if (!showLabel) {
    return (
      <div
        className={`
          inline-flex items-center justify-center rounded-full border
          ${config.bgColor} ${config.borderColor} ${sizeClasses.container}
        `}
        role="img"
        aria-label={ariaLabel || `Priority: ${config.description}`}
        title={config.description}
      >
        <Icon 
          className={`${config.iconColor} ${sizeClasses.icon}`} 
          aria-hidden="true"
        />
      </div>
    );
  }

  // Full version with label
  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-medium border
        ${config.bgColor} ${config.textColor} ${config.borderColor} ${sizeClasses.container}
      `}
      role="img"
      aria-label={ariaLabel || `Priority: ${config.description}`}
      title={config.description}
    >
      <Icon 
        className={`${config.iconColor} ${sizeClasses.icon} flex-shrink-0`} 
        aria-hidden="true"
      />
      <span>{config.label}</span>
    </span>
  );
};

export default PriorityIndicator;